import { isBrowser } from "./browser";
import { API_URL } from "./Api";

export const BUYER_TOKEN_STORAGE_KEY = "buyer_access_token";
export const BUYER_USER_STORAGE_KEY = "buyer_user";
const BUYER_API_STORAGE_KEY = "buyer_api_base_url";
export const SELLER_TOKEN_STORAGE_KEY = "seller_access_token";
export const SELLER_USER_STORAGE_KEY = "seller_user";
export const BUYER_AUTH_EVENT = "elumia:buyer-auth";
export const SELLER_AUTH_EVENT = "elumia:seller-auth";

const LEGACY_USER_STORAGE_KEY = "user";
const LEGACY_TOKEN_STORAGE_KEYS = ["token", "authToken"];
const SELLER_ROLE_ID = 2;
const BUYER_ROLE_ID = 3;

const dispatchAuthEvent = (eventName) => {
  if (!isBrowser) {
    return;
  }

  window.dispatchEvent(new Event(eventName));
  window.dispatchEvent(new Event("storage"));
};

const getRawStorageItem = (key) => {
  if (!isBrowser) {
    return "";
  }

  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const setRawStorageItem = (key, value) => {
  if (!isBrowser) {
    return;
  }

  try {
    localStorage.setItem(key, value);
  } catch {}
};

const removeRawStorageItem = (key) => {
  if (!isBrowser) {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch {}
};

const getJsonStorageItem = (key) => {
  const value = getRawStorageItem(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const decodeJwtPayload = (token = "") => {
  const parts = String(token || "").split(".");

  if (parts.length < 2) {
    return null;
  }

  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4 || 4)) % 4),
      "=",
    );

    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const isSellerIdentity = (value) => {
  const normalizedRole = String(value?.role || "").trim().toLowerCase();
  const normalizedTokenType = String(value?.token_type || "")
    .trim()
    .toLowerCase();

  return (
    Number(value?.role_id) === SELLER_ROLE_ID ||
    normalizedRole === "seller" ||
    normalizedTokenType === "seller"
  );
};

const findLegacyToken = () => {
  for (const key of LEGACY_TOKEN_STORAGE_KEYS) {
    const token = getRawStorageItem(key);

    if (token) {
      return { key, token };
    }
  }

  return null;
};

const removeLegacyAuthStorage = () => {
  LEGACY_TOKEN_STORAGE_KEYS.forEach(removeRawStorageItem);
  removeRawStorageItem(LEGACY_USER_STORAGE_KEY);
};

const setBuyerUser = (user, { notify = true } = {}) => {
  if (user) {
    setRawStorageItem(BUYER_USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    removeRawStorageItem(BUYER_USER_STORAGE_KEY);
  }

  if (notify) {
    dispatchAuthEvent(BUYER_AUTH_EVENT);
  }
};

export const setSellerUser = (user, { notify = true } = {}) => {
  if (user) {
    setRawStorageItem(SELLER_USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    removeRawStorageItem(SELLER_USER_STORAGE_KEY);
  }

  if (notify) {
    dispatchAuthEvent(SELLER_AUTH_EVENT);
  }
};

export const migrateLegacyAuthStorage = () => {
  if (!isBrowser) {
    return;
  }

  const legacyTokenEntry = findLegacyToken();

  if (!legacyTokenEntry) {
    return;
  }

  const legacyUser = getJsonStorageItem(LEGACY_USER_STORAGE_KEY);
  const tokenPayload = decodeJwtPayload(legacyTokenEntry.token);
  const shouldTreatAsSeller = isSellerIdentity(legacyUser) || isSellerIdentity(tokenPayload);

  if (shouldTreatAsSeller && !getRawStorageItem(SELLER_TOKEN_STORAGE_KEY)) {
    setRawStorageItem(SELLER_TOKEN_STORAGE_KEY, legacyTokenEntry.token);

    if (legacyUser) {
      setRawStorageItem(SELLER_USER_STORAGE_KEY, JSON.stringify(legacyUser));
    }
  }

  if (!shouldTreatAsSeller && !getRawStorageItem(BUYER_TOKEN_STORAGE_KEY)) {
    setRawStorageItem(BUYER_TOKEN_STORAGE_KEY, legacyTokenEntry.token);

    if (legacyUser) {
      setRawStorageItem(BUYER_USER_STORAGE_KEY, JSON.stringify(legacyUser));
    }
  }

  removeLegacyAuthStorage();
};

export const getBuyerToken = () => {
  migrateLegacyAuthStorage();
  const token = getRawStorageItem(BUYER_TOKEN_STORAGE_KEY);
  const savedApiUrl = getRawStorageItem(BUYER_API_STORAGE_KEY);
  const isLocalDevelopment =
    process.env.NODE_ENV === "development" &&
    API_URL.api.startsWith("http://localhost:");

  if (token && (savedApiUrl ? savedApiUrl !== API_URL.api : isLocalDevelopment)) {
    clearBuyerSession();
    return "";
  }

  return token;
};

export const getSellerToken = () => {
  migrateLegacyAuthStorage();
  return getRawStorageItem(SELLER_TOKEN_STORAGE_KEY);
};

export const getBuyerUser = () => {
  migrateLegacyAuthStorage();
  return getJsonStorageItem(BUYER_USER_STORAGE_KEY);
};

export const getSellerUser = () => {
  migrateLegacyAuthStorage();
  return getJsonStorageItem(SELLER_USER_STORAGE_KEY);
};

export const setBuyerSession = (payload = {}) => {
  if (!payload?.token) {
    return;
  }

  removeLegacyAuthStorage();
  setRawStorageItem(BUYER_TOKEN_STORAGE_KEY, payload.token);
  setRawStorageItem(BUYER_API_STORAGE_KEY, API_URL.api);
  setBuyerUser(payload.user || null, { notify: false });
  dispatchAuthEvent(BUYER_AUTH_EVENT);
};

export const setSellerSession = (payload = {}) => {
  if (!payload?.token) {
    return;
  }

  removeLegacyAuthStorage();
  setRawStorageItem(SELLER_TOKEN_STORAGE_KEY, payload.token);
  setSellerUser(payload.user || null, { notify: false });
  dispatchAuthEvent(SELLER_AUTH_EVENT);
};

export const clearBuyerSession = () => {
  removeRawStorageItem(BUYER_TOKEN_STORAGE_KEY);
  removeRawStorageItem(BUYER_USER_STORAGE_KEY);
  removeRawStorageItem(BUYER_API_STORAGE_KEY);

  const legacyTokenEntry = findLegacyToken();
  const legacyUser = getJsonStorageItem(LEGACY_USER_STORAGE_KEY);

  if (
    legacyTokenEntry &&
    (isSellerIdentity(legacyUser) || isSellerIdentity(decodeJwtPayload(legacyTokenEntry.token)))
  ) {
    // Preserve any seller-only legacy residue until it can be migrated explicitly.
  } else {
    removeLegacyAuthStorage();
  }

  dispatchAuthEvent(BUYER_AUTH_EVENT);
};

export const clearSellerSession = () => {
  removeRawStorageItem(SELLER_TOKEN_STORAGE_KEY);
  removeRawStorageItem(SELLER_USER_STORAGE_KEY);

  const legacyTokenEntry = findLegacyToken();
  const legacyUser = getJsonStorageItem(LEGACY_USER_STORAGE_KEY);

  if (
    legacyTokenEntry &&
    !isSellerIdentity(legacyUser) &&
    !isSellerIdentity(decodeJwtPayload(legacyTokenEntry.token))
  ) {
    // Preserve any buyer-only legacy residue until it can be migrated explicitly.
  } else {
    removeLegacyAuthStorage();
  }

  dispatchAuthEvent(SELLER_AUTH_EVENT);
};

export const isBuyerAuthenticated = () => Boolean(getBuyerToken());
export const isSellerAuthenticated = () => Boolean(getSellerToken());

export const getSellerTokenClaims = () => decodeJwtPayload(getSellerToken());
export const getBuyerTokenClaims = () => decodeJwtPayload(getBuyerToken());

export const getDefaultBuyerRoleId = () => BUYER_ROLE_ID;
export const getDefaultSellerRoleId = () => SELLER_ROLE_ID;
